import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Home, Compass } from "lucide-react";
import { useSEO } from "@/lib/seo";

export default function NotFound() {
  useSEO({
    title: "404 — Off the map | RockLadder",
    description: "This route doesn't exist. Try the home page or search.",
  });

  return (
    <div className="relative min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-24">
      <div className="text-center max-w-2xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative inline-block"
        >
          <div className="relative flex items-center justify-center gap-4 md:gap-6 mb-6">
            <img
              src="/brand/rockladder-logo-icon.png"
              alt=""
              aria-hidden="true"
              className="h-16 md:h-24 w-auto drop-shadow-[0_0_18px_rgba(139,92,246,0.35)] animate-[chevron-bob_2.4s_ease-in-out_infinite]"
            />
          </div>
          <div className="relative font-display font-semibold leading-none tracking-[-0.02em]" style={{ fontSize: "clamp(6rem, 18vw, 14rem)" }}>
            <span className="text-gradient-gold-violet" data-testid="glitch-404-base">404</span>
            {/* Glitch layers */}
            <span
              aria-hidden="true"
              className="absolute inset-0 text-gold mix-blend-screen opacity-70 animate-[chevron-bob_2s_ease-in-out_infinite]"
              style={{ transform: "translate(-3px, 2px)", clipPath: "polygon(0 20%, 100% 20%, 100% 45%, 0 45%)" }}
            >
              404
            </span>
            <span
              aria-hidden="true"
              className="absolute inset-0 text-violet2 mix-blend-screen opacity-70"
              style={{ transform: "translate(3px, -1px)", clipPath: "polygon(0 55%, 100% 55%, 100% 80%, 0 80%)", animation: "chevron-bob 2.4s ease-in-out infinite reverse" }}
            >
              404
            </span>
          </div>
        </motion.div>

        <div className="mt-6 font-mono text-[11px] tracking-[0.32em] text-gold-body">
          SIGNAL LOST · ROUTE UNKNOWN
        </div>
        <h1 className="mt-4 font-display text-2xl md:text-3xl text-inkText-primary">
          This URL never made it into the runbook.
        </h1>
        <p className="mt-4 text-sm md:text-base text-inkText-muted max-w-md mx-auto">
          The page you&apos;re looking for doesn&apos;t exist or was moved. Let&apos;s get you back to somewhere useful.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/" data-testid="404-home" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-[#FFD37A] to-[#C89026] text-ink px-5 py-2.5 text-sm font-medium border border-gold/60">
            <Home className="h-4 w-4" /> Back to home
          </Link>
          <Link to="/services" data-testid="404-services" className="inline-flex items-center gap-2 rounded-full border border-gold/25 hover:border-gold text-inkText-primary hover:text-gold px-5 py-2.5 text-sm transition-colors">
            <Compass className="h-4 w-4" /> Explore services
          </Link>
          <button
            onClick={() => window.history.back()}
            data-testid="404-back"
            className="inline-flex items-center gap-2 rounded-full text-sm text-inkText-muted hover:text-gold px-5 py-2.5"
          >
            <ArrowLeft className="h-4 w-4" /> Go back
          </button>
        </div>
      </div>
    </div>
  );
}
